import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { XCircle, Loader } from "lucide-react";
import styles from "./LoginPage.module.css";
import { useTranslation } from "react-i18next";
import { PLATFORM_BRAND } from "../../config/platformBrand";
import YulqoMark from "../../components/ui/YulqoMark";

type ApiErrorBody = {
  code?: string;
  error?: string;
};

const AUTH_ERROR_KEYS: Record<string, string> = {
  AUTH_INVALID_CREDENTIALS: "admin.login.invalidCredentials",
  AUTH_FIELDS_REQUIRED: "admin.login.fieldsRequired",
  AUTH_TOKEN_INVALID: "admin.login.error",
  AUTH_REGISTER_DISABLED: "admin.login.error",
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });

  const mapAuthError = useCallback(
    (body?: ApiErrorBody): string => {
      if (body?.code && AUTH_ERROR_KEYS[body.code]) {
        return t(AUTH_ERROR_KEYS[body.code]);
      }
      return t("admin.login.error");
    },
    [t],
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    setLoginData({ email, password });
    if (!email || !password) {
      setError(t("admin.login.fieldsRequired"));
      return;
    }
    setLoading(true);

    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: ApiErrorBody } };
      setError(mapAuthError(axiosErr.response?.data));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.loginBrand}>
        <YulqoMark size={36} />
        <span className={styles.loginWordmark}>{PLATFORM_BRAND.productName}</span>
      </div>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("admin.login.title")}</h1>
          <p className={styles.subtitle}>{t("admin.login.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div>
            <label htmlFor="email" className={styles.label}>
              {t("admin.login.email")}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={loginData.email}
              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
              className={styles.input}
              placeholder={t("admin.login.emailPlaceholder")}
            />
          </div>

          <div>
            <label htmlFor="password" className={styles.label}>
              {t("admin.login.password")}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              className={styles.input}
              placeholder={t("admin.login.passwordPlaceholder")}
            />
          </div>

          {error && (
            <div className={styles.error}>
              <XCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <button type="submit" disabled={loading} className={styles.button}>
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" aria-hidden="true" />
                {t("common.loading")}
              </>
            ) : (
              t("admin.login.submit")
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <p className={styles.subtitle}>
            {t("admin.login.noAccount")}{" "}
            <Link to="/admin/register" className={styles.backLink}>
              {t("admin.login.createAccount")}
            </Link>
          </p>
          <Link to="/" className={styles.backLink}>
            ← {t("common.back")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
